import React, { Component } from "react";
import PropTypes from "prop-types";

import LineSeries from "./LineSeries";
import StraightLine from "./StraightLine";
import AreaOnlySeries from "./AreaOnlySeries";

const DEFAULT_OVERBOUGHT_VALUE = -20;
const DEFAULT_OVERSOLD_VALUE = -80;
const DEFAULT_THRESHOLD_STROKE = {
  color: "#B8C2CC",
  dash: "Dash",
  width: 1,
  opacity: 1,
};
const DEFAULT_SERIES_STROKE = {
  color: "#808080",
  dash: "Solid",
  width: 1,
  opacity: 1,
};

const cloneStroke = (strokeCandidate, fallback, thresholdSource) => {
  const source = strokeCandidate?.line || strokeCandidate || {};
  const colorCandidate =
    source.color ??
    thresholdSource?.color ??
    (typeof strokeCandidate === "string" ? strokeCandidate : undefined);

  const dashCandidate = source.dash ?? thresholdSource?.dash;
  const widthCandidate = source.width ?? thresholdSource?.width;
  const opacityCandidate = source.opacity ?? thresholdSource?.opacity;

  return {
    color: colorCandidate ?? fallback.color,
    dash: dashCandidate ?? fallback.dash,
    width: widthCandidate ?? fallback.width,
    opacity: opacityCandidate ?? fallback.opacity,
  };
};

const unwrapThreshold = (input) => {
  if (!input || typeof input !== "object") {
    return input;
  }

  if (
    Number.isFinite(input.value) ||
    Number.isFinite(input.threshold) ||
    input.stroke ||
    input.fill ||
    input.color
  ) {
    return input;
  }

  const candidate = Object.values(input).find((value) => {
    if (!value || typeof value !== "object") return false;
    return (
      Number.isFinite(value.value) ||
      Number.isFinite(value.threshold) ||
      value.stroke ||
      value.fill ||
      value.color
    );
  });

  return candidate || input;
};

const resolveThreshold = (threshold, defaultValue) => {
  const normalized = unwrapThreshold(threshold);

  const baseValue = Number.isFinite(normalized?.value)
    ? normalized.value
    : Number.isFinite(normalized)
      ? normalized
      : Number.isFinite(normalized?.threshold)
        ? normalized.threshold
        : defaultValue;

  const strokeSource = normalized?.stroke || normalized?.line || normalized;
  const stroke = cloneStroke(
    strokeSource,
    DEFAULT_THRESHOLD_STROKE,
    normalized,
  );

  const fill =
    normalized?.fill ||
    strokeSource?.fill ||
    normalized?.color ||
    (typeof normalized === "string" ? normalized : undefined) ||
    stroke.color;

  return {
    value: baseValue,
    stroke,
    fill,
  };
};

class WilliamsRSeries extends Component {
  render() {
    const {
      className,
      yAccessor,
      stroke,
      showZones,
      overBought,
      overSold,
      highlightOnHover,
      onDoubleClick,
      onContextMenu,
      onHover,
      onUnHover,
    } = this.props;

    const resolvedStroke = cloneStroke(stroke, DEFAULT_SERIES_STROKE);

    const {
      value: overBoughtValue,
      stroke: overBoughtStroke,
      fill: overBoughtFillColor,
    } = resolveThreshold(overBought, DEFAULT_OVERBOUGHT_VALUE);
    const {
      value: overSoldValue,
      stroke: overSoldStroke,
      fill: overSoldFillColor,
    } = resolveThreshold(overSold, DEFAULT_OVERSOLD_VALUE);

    const overBoughtFillOpacity = 0.1;
    const overSoldFillOpacity = 0.1;

    const overBoughtAreaAccessor = (d) => {
      const value = yAccessor(d);
      if (value === undefined || value === null) return undefined;
      return value >= overBoughtValue ? value : overBoughtValue;
    };

    const overSoldAreaAccessor = (d) => {
      const value = yAccessor(d);
      if (value === undefined || value === null) return undefined;
      return value <= overSoldValue ? value : overSoldValue;
    };

    return (
      <g className={className}>
        <LineSeries
          yAccessor={yAccessor}
          stroke={resolvedStroke}
          highlightOnHover={highlightOnHover}
          onDoubleClick={onDoubleClick}
          onContextMenu={onContextMenu}
          onHover={onHover}
          onUnHover={onUnHover}
        />
        {showZones !== false && (
          <>
            <StraightLine
              stroke={overBoughtStroke}
              yValue={overBoughtValue}
              highlightOnHover={false}
            />
            <StraightLine
              stroke={overSoldStroke}
              yValue={overSoldValue}
              highlightOnHover={false}
            />
            <AreaOnlySeries
              yAccessor={overBoughtAreaAccessor}
              base={(yScale) => yScale(overBoughtValue)}
              fill={overBoughtFillColor}
              opacity={overBoughtFillOpacity}
              stroke={{ color: "none" }}
            />
            <AreaOnlySeries
              yAccessor={overSoldAreaAccessor}
              base={(yScale) => yScale(overSoldValue)}
              fill={overSoldFillColor}
              opacity={overSoldFillOpacity}
              stroke={{ color: "none" }}
            />
          </>
        )}
      </g>
    );
  }
}

WilliamsRSeries.propTypes = {
  className: PropTypes.string,
  yAccessor: PropTypes.func.isRequired,
  stroke: PropTypes.oneOfType([PropTypes.object, PropTypes.string]).isRequired,
  showZones: PropTypes.bool,
  overBought: PropTypes.oneOfType([
    PropTypes.shape({
      value: PropTypes.number,
      stroke: PropTypes.object,
    }),
    PropTypes.number,
    PropTypes.string,
  ]),
  overSold: PropTypes.oneOfType([
    PropTypes.shape({
      value: PropTypes.number,
      stroke: PropTypes.object,
    }),
    PropTypes.number,
    PropTypes.string,
  ]),
  highlightOnHover: PropTypes.bool,
  onDoubleClick: PropTypes.func,
  onContextMenu: PropTypes.func,
  onHover: PropTypes.func,
  onUnHover: PropTypes.func,
};

WilliamsRSeries.defaultProps = {
  className: "react-stockcharts-williamsr-series",
  stroke: { ...DEFAULT_SERIES_STROKE },
  showZones: true,
  overBought: {
    value: DEFAULT_OVERBOUGHT_VALUE,
    stroke: { ...DEFAULT_THRESHOLD_STROKE },
  },
  overSold: {
    value: DEFAULT_OVERSOLD_VALUE,
    stroke: { ...DEFAULT_THRESHOLD_STROKE },
  },
  highlightOnHover: true,
};

export default WilliamsRSeries;
